/**
 * B57 (TRA-81) — the journey planner, pure. B56 put the route truth in the
 * supply (per-leg slots, leg-scoped consumption, O-D pair products derived
 * from ONE definition) and slice 1 put a display descriptor on each O-D
 * card (presentation.journey: named endpoints + origin-first leg keys).
 * This module is the storefront-side vocabulary over those cards:
 *
 * - ROUTE GROUPS: journey cards sharing a schedule binding are one
 *   direction of a route; ordered stops are rebuilt from the leg chain
 *   (a fact of the legs, never of card order).
 * - PICKERS: origins, destinations-from, and the exact O-D card.
 * - ROUND TRIP: the reversed O-D card, when the catalog sells it — a
 *   one-directional route honestly has no round trips.
 * - DEPARTURES: the picker lists the journey's BOARDING (origin) leg slots;
 *   cart.add takes any leg slot and checkout resolves the siblings
 *   atomically (B56) — the UI never re-derives commerce truth.
 * - ARRIVAL: display-only, from the final-leg sibling of the same run;
 *   absent stays absent (honest absence, never a guess).
 * - THE PLAN: one way = the composer's plan; round trip = both directions'
 *   lines in ONE cart, so checkout's atomic hold covers the whole trip and
 *   the pair promotion (PERCENT_OFF automatic, min-qty across directions)
 *   prices it — no new commerce machinery.
 *
 * Availability stays honest: seat chips describe the displayed boarding-leg
 * slot only; a full later leg refuses the whole journey at cart/checkout
 * (Inv 2/3) and B31g translates the refusal. TICKETS_FIRST exact-fit
 * calendars stay gated on the §2.4 ruling (B31h(a)).
 */
import { addPlan, totalQuantity, type AddPlanLine, type Composition } from "./composition.js";
import type { ProductSummary, SlotSummary } from "../ui/screens.js";

export interface JourneyEndpoint {
  readonly key: string;
  readonly name: string;
}

export interface Journey {
  readonly from: JourneyEndpoint;
  readonly to: JourneyEndpoint;
  /** Ordered leg keys (`<from>~<to>`), origin first — slice 1's descriptor. */
  readonly legs: readonly string[];
}

/** The card's journey descriptor; undefined for non-journey products. */
export function journeyOf(card: ProductSummary): Journey | undefined {
  return card.presentation?.journey;
}

/* ------------------------------------------------------------- grouping */

export interface RouteGroup {
  /** Group identity: the sorted schedule binding (one direction of a route). */
  readonly key: string;
  readonly scheduleIds: readonly string[];
  /** Ordered stops, origin end first, rebuilt from the single-leg chain. */
  readonly stops: readonly JourneyEndpoint[];
  /** The direction's O-D cards, catalog order. */
  readonly cards: readonly ProductSummary[];
}

function groupKeyOf(card: ProductSummary): string {
  return [...card.scheduleIds].sort().join(" ");
}

/** Ordered stops from the group's legs: follow the chain from the stop no leg arrives at. */
function orderedStops(cards: readonly ProductSummary[]): readonly JourneyEndpoint[] {
  const names = new Map<string, string>();
  const next = new Map<string, string>();
  const arrivals = new Set<string>();
  for (const card of cards) {
    const j = journeyOf(card);
    if (j === undefined) continue;
    names.set(j.from.key, j.from.name);
    names.set(j.to.key, j.to.name);
    for (const leg of j.legs) {
      const tilde = leg.indexOf("~");
      if (tilde <= 0 || tilde === leg.length - 1) continue;
      const from = leg.slice(0, tilde);
      const to = leg.slice(tilde + 1);
      next.set(from, to);
      arrivals.add(to);
    }
  }
  const start = [...next.keys()].find((k) => !arrivals.has(k));
  if (start === undefined) return []; // malformed (cyclic) — the planner steps aside
  const stops: JourneyEndpoint[] = [];
  const seen = new Set<string>();
  for (let key: string | undefined = start; key !== undefined && !seen.has(key); key = next.get(key)) {
    seen.add(key);
    stops.push({ key, name: names.get(key) ?? key });
  }
  return stops;
}

/** Journey cards grouped into route directions, deterministic order. */
export function routeGroups(cards: readonly ProductSummary[]): readonly RouteGroup[] {
  const byKey = new Map<string, ProductSummary[]>();
  for (const card of cards) {
    if (journeyOf(card) === undefined || card.scheduleIds.length === 0) continue;
    const key = groupKeyOf(card);
    byKey.set(key, [...(byKey.get(key) ?? []), card]);
  }
  return [...byKey.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, group]) => ({
      key,
      scheduleIds: [...group[0]!.scheduleIds],
      stops: orderedStops(group),
      cards: group,
    }));
}

/* -------------------------------------------------------------- pickers */

/** Stops a journey departs from, stop order. */
export function origins(group: RouteGroup): readonly JourneyEndpoint[] {
  const froms = new Set(group.cards.map((c) => journeyOf(c)?.from.key).filter((k) => k !== undefined));
  return group.stops.filter((s) => froms.has(s.key));
}

/** Destinations reachable from an origin, stop order. */
export function destinationsFrom(group: RouteGroup, fromKey: string): readonly JourneyEndpoint[] {
  const tos = new Set(
    group.cards
      .map((c) => journeyOf(c))
      .filter((j): j is Journey => j !== undefined && j.from.key === fromKey)
      .map((j) => j.to.key),
  );
  return group.stops.filter((s) => tos.has(s.key));
}

/** Exactly the O-D card, or honestly nothing. */
export function cardFor(group: RouteGroup, fromKey: string, toKey: string): ProductSummary | undefined {
  return group.cards.find((c) => {
    const j = journeyOf(c);
    return j !== undefined && j.from.key === fromKey && j.to.key === toKey;
  });
}

/** The reversed O-D card across ALL groups — undefined = no round trip sold. */
export function returnCardFor(groups: readonly RouteGroup[], card: ProductSummary): ProductSummary | undefined {
  const j = journeyOf(card);
  if (j === undefined) return undefined;
  for (const group of groups) {
    const reversed = cardFor(group, j.to.key, j.from.key);
    if (reversed !== undefined) return reversed;
  }
  return undefined;
}

/* The planner's O-D matrix (plannerOrigins/plannerDestinations/plannerCard)
   retired with the planner face (B403 #28 — 75:2 draws one card per product,
   routes included). returnCardFor above still powers ROUND TRIP in the walk. */

/* ---------------------------------------------------------------- slots */

const LEG_MARKER = "#leg:";

/** The leg key a per-leg slot id carries; plain slot ids carry none. */
export function slotLegKey(slotId: string): string | undefined {
  const i = slotId.lastIndexOf(LEG_MARKER);
  if (i < 0) return undefined;
  const key = slotId.slice(i + LEG_MARKER.length);
  return key.includes("~") ? key : undefined;
}

/** The run's base id (B56: leg ids extend the run's base id). */
export function runBaseOf(slotId: string): string {
  const i = slotId.lastIndexOf(LEG_MARKER);
  return i < 0 ? slotId : slotId.slice(0, i);
}

/** The leg the journey boards at — its origin leg (B56: tokens bind it too). */
export function boardingLegOf(journey: Journey): string {
  return journey.legs[0]!;
}

/** The schedule's slots narrowed to the journey's boarding leg — the departure picker's list. */
export function journeyDepartures(slots: readonly SlotSummary[], journey: Journey): readonly SlotSummary[] {
  const boarding = boardingLegOf(journey);
  return slots.filter((s) => slotLegKey(s.slotId) === boarding);
}

/**
 * Display-only arrival: the end of the FINAL leg's sibling slot on the same
 * run. Absent when the sibling is not in the list — unknown stays unknown.
 */
export function journeyArrival(
  slots: readonly SlotSummary[],
  journey: Journey,
  boardingSlotId: string,
): number | undefined {
  const finalLeg = journey.legs[journey.legs.length - 1]!;
  const base = runBaseOf(boardingSlotId);
  return slots.find((s) => runBaseOf(s.slotId) === base && slotLegKey(s.slotId) === finalLeg)?.end;
}

/** Calendar-day key (YYYY-MM-DD, display zone) — the return picker's same-day default. */
export function sameDayKey(startMs: number, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat("en-CA", {
    ...(timeZone !== undefined ? { timeZone } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(startMs);
}

/* ----------------------------------------------------------------- plan */

export interface DirectionChoice {
  readonly card: ProductSummary;
  readonly slotId: string;
  readonly comp: Composition;
}

/**
 * The whole trip as ONE add plan: outbound lines, then return lines, each
 * direction riding its own slot — one cart, one atomic checkout hold.
 * Walls, not signs: a return that does not reverse the outbound O-D, an
 * empty return party, or plan cards without journeys are caller mistakes.
 */
export function journeyAddPlan(outbound: DirectionChoice, ret?: DirectionChoice): readonly AddPlanLine[] {
  if (ret === undefined) return addPlan(outbound.card, outbound.comp, outbound.slotId);
  const out = journeyOf(outbound.card);
  const back = journeyOf(ret.card);
  if (out === undefined || back === undefined) throw new Error("round trips need journey cards on both directions");
  if (back.from.key !== out.to.key || back.to.key !== out.from.key) {
    throw new Error(
      `the return must reverse the outbound O-D: ${out.from.key}→${out.to.key} vs ${back.from.key}→${back.to.key}`,
    );
  }
  if (totalQuantity(ret.comp) === 0) throw new Error("a round trip with an empty return party is a mistake");
  return [...addPlan(outbound.card, outbound.comp, outbound.slotId), ...addPlan(ret.card, ret.comp, ret.slotId)];
}

/* --------------------------------------------------------- B170 card price */

/** B170: a card price fact — the family minimum, flagged as a "from" price. */
export interface CardPriceFact {
  readonly amount: { readonly amount: number; readonly currency: string };
  readonly from: boolean;
}

/**
 * B170 (TRA-206) — grid-card price honesty. The QA walk saw the parent
 * ferry card say "Adult · $12.00" while the chosen crossing's composer
 * said "$8.00 each": route (O-D) cards carry their own prices, and a
 * buyer read three different numbers in ninety seconds with nothing
 * explaining any of them. When a grid card's journey FAMILY (the route
 * groups sharing its schedule binding) prices a same-named variant
 * differently, the card face must say "from <min>" instead of an exact
 * number that no journey may actually charge. Journey cards themselves
 * price exactly and get no fact; so do cards with no varying family.
 */
export function cardPriceFacts(
  products: readonly ProductSummary[],
): ReadonlyMap<string, ReadonlyMap<string, CardPriceFact>> {
  const groups = routeGroups(products);
  const facts = new Map<string, Map<string, CardPriceFact>>();
  for (const card of products) {
    if (journeyOf(card) !== undefined) continue; // an O-D card's price is exact
    const family = groups.filter((g) => g.scheduleIds.some((id) => card.scheduleIds.includes(id)));
    if (family.length === 0) continue;
    const perVariant = new Map<string, CardPriceFact>();
    for (const v of card.variants) {
      if (v.displayPrice === undefined) continue;
      const prices = [v.displayPrice.amount];
      for (const g of family)
        for (const c of g.cards)
          for (const cv of c.variants)
            if (cv.name === v.name && cv.displayPrice !== undefined && cv.displayPrice.currency === v.displayPrice.currency)
              prices.push(cv.displayPrice.amount);
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      if (min < max) perVariant.set(v.variantId, { amount: { amount: min, currency: v.displayPrice.currency }, from: true });
    }
    if (perVariant.size > 0) facts.set(card.productId, perVariant);
  }
  return facts;
}
